import type { BlogPost } from "./posts";

export const beginnerPosts: BlogPost[] = [
  {
    slug: "getting-started-with-omnicanvas",
    title: "Getting Started with OmniCanvas: A Beginner's Tutorial",
    description:
      "A step-by-step walkthrough for new users: your first note, drawing on the canvas, adding sticky notes, organizing with folders and tags, and the views.",
    date: "2027-02-21",
    readTime: "8 min read",
    category: "Getting Started",
    content: `
## Welcome to OmniCanvas

OmniCanvas is an infinite spatial notetaking app that lets you think visually. Instead of writing notes in a linear document, you place ideas, drawings, and sticky notes on a boundless two-dimensional canvas. If you have ever felt limited by traditional note apps, you are in the right place.

This tutorial walks you through everything you need to know to start using OmniCanvas confidently. By the end, you will have created your first note, drawn on the canvas, added sticky notes, organized your work with folders and tags, and explored the different views available to you.

## Step 1: Create Your First Note

When you open OmniCanvas for the first time, you will see an empty workspace. To create your first note, click the **New Note** button. A fresh infinite canvas opens up immediately — this is your blank slate.

Every note in OmniCanvas is a canvas. There are no separate "document" and "drawing" modes. Everything lives together on the same surface, which means you can mix freehand sketches, typed text, shapes, and sticky notes in a single note.

Give your note a title by clicking the title area at the top. Choose something descriptive so you can find it later — titles are fully searchable.

## Step 2: Draw on the Canvas

The canvas is powered by Excalidraw, a powerful drawing engine that feels natural and responsive. Along the toolbar you will find tools for:

- **Rectangles** — great for outlining sections or creating simple diagrams
- **Ellipses** — useful for mind map nodes or highlighting concepts
- **Arrows and Lines** — connect ideas visually and show relationships
- **Freehand Drawing** — sketch anything with a pencil-like tool
- **Text** — click anywhere and start typing to place text directly on the canvas

Try drawing a few shapes and connecting them with arrows. You will notice that arrows snap to shape edges, making it easy to create clean diagrams without fussing over alignment.

You can select any element, resize it, change its color, or move it around. The canvas is infinite, so you never run out of room. Zoom in for detail work and zoom out to see the big picture.

## Step 3: Add Sticky Notes

Sticky notes are one of the most useful features in OmniCanvas. They sit on top of your canvas and support rich text editing, making them perfect for longer-form writing alongside your visual content.

To add a sticky note, use the sticky note tool in the toolbar. Click anywhere on the canvas to place it, then start typing. You can choose from **six different colors** to visually categorize your sticky notes — for example, yellow for ideas, blue for questions, and green for action items.

Sticky notes are fully searchable. When you use the search feature later, OmniCanvas looks inside your sticky note text along with everything else.

## Step 4: Organize with Folders

As you create more notes, you will want to keep them organized. OmniCanvas uses a **hierarchical folder system** that works just like folders on your computer.

Click on **Folders** in the sidebar to create your first folder. You might start with broad categories like "Work," "Personal," and "Learning." You can nest folders inside each other to create deeper organization — for example, "Work > Project Alpha > Meeting Notes."

To move a note into a folder, select the note and choose the folder from the note options. A single note can live in multiple folders if it belongs to more than one category.

## Step 5: Add Tags for Flexible Organization

While folders give you rigid hierarchy, **tags** give you flexible cross-cutting organization. You can add tags to any note, and a note can have as many tags as you like.

Good starter tags might include things like "important," "in-progress," "reference," or "idea." Tags work alongside folders, so you can organize the same notes in two different ways simultaneously.

Tags are also searchable and appear as connections in the knowledge graph, which we will explore in a moment.

## Step 6: Explore Different Views

One of the things that makes OmniCanvas unique is that you can view your notes in multiple ways. Each view offers a different perspective on the same underlying data:

- **Grid View** — see your notes as visual cards in a grid layout, perfect for browsing
- **List View** — a compact list with titles and metadata, great for scanning quickly
- **Table View** — a spreadsheet-like view with sortable columns for dates, tags, and folders
- **Kanban View** — organize notes into columns, ideal for tracking project stages
- **Graph View** — a knowledge graph that visualizes connections between notes, folders, and tags

Try switching between views to see how they change your perspective. Many users find that different views are useful for different tasks — grid for creative browsing, list for quick lookup, kanban for project management.

## Step 7: Search Everything with Cmd+K

Press **Cmd+K** (or Ctrl+K on Windows and Linux) to open the universal search. This is one of the fastest ways to navigate your workspace.

OmniCanvas search looks through note titles, tags, folder names, canvas text, and sticky note content. Start typing and results appear instantly. Select a result to jump straight to that note.

As your collection grows, search becomes indispensable. Get in the habit of using it early so it becomes second nature.

## Next Steps

You now know the fundamentals of OmniCanvas. Here are a few things to try next:

- **Pin important notes** so they always appear at the top of your list
- **Try dark mode** from the settings if you prefer a darker interface
- **Export a note to PDF** to share your canvas with someone who does not use OmniCanvas
- **Enable cloud sync** to keep your notes available across devices
- **Explore the knowledge graph** to discover connections you did not know existed

For deeper dives, follow our [step-by-step first canvas walkthrough](/blog/getting-started/first-canvas-step-by-step), learn how to [set up your workspace for maximum productivity](/blog/getting-started/set-up-omnicanvas-workspace), and if you are on Windows or Linux, see [using OmniCanvas on the web](/blog/getting-started/using-omnicanvas-web-app).

The best way to learn OmniCanvas is to use it for something real. Pick a project, a course, or a personal goal and start capturing your thoughts on the canvas. The spatial layout will help you see relationships between ideas that linear notes would hide.
`,
  },
  {
    slug: "first-canvas-step-by-step",
    title: "Your First Canvas: A Step-by-Step Walkthrough",
    description:
      "A focused tutorial on creating your first canvas note — drawing shapes, adding text, using sticky notes, and saving your work.",
    date: "2027-02-28",
    readTime: "6 min read",
    category: "Getting Started",
    content: `
## Creating Your First Canvas

This is a focused, hands-on tutorial. By the end, you will have a canvas note with shapes, text, connections, and sticky notes. Open the [free online whiteboard](/tools/whiteboard) and follow along — and if you want the full feature tour first, start with our [beginner's guide to OmniCanvas](/blog/getting-started/getting-started-with-omnicanvas).

## Start a New Note

Click the **New Note** button to create a fresh canvas. Give it a title — something like "My First Canvas" works fine for now. The title field is at the top of the screen. You can always rename it later.

You are now looking at an infinite canvas. There is no edge, no page boundary, and no limit to how much you can put here. Scroll by clicking and dragging on empty space, or use a trackpad gesture to pan around. Pinch to zoom or use Ctrl+Scroll to zoom in and out.

## Draw Your First Shape

Select the **rectangle tool** from the toolbar. Click and drag anywhere on the canvas to draw a rectangle. Do not worry about getting it perfect — you can resize and reposition it at any time.

Now try changing the rectangle. Click on it to select it, and you will see handles on the corners and edges. Drag a corner to resize. Use the color options in the toolbar to change the fill or stroke color. Try making it a soft blue with a darker border.

Draw a second rectangle nearby. Position it to the right of the first one, leaving a gap between them.

## Connect Shapes with Arrows

Select the **arrow tool** from the toolbar. Click on the edge of your first rectangle and drag to the edge of your second rectangle. The arrow automatically snaps to the shape boundaries, creating a clean connection.

This is the foundation of diagramming in OmniCanvas. Arrows maintain their connections when you move shapes around. Try dragging one of your rectangles to a new position — the arrow follows.

Create a few more shapes and connect them. Try using **ellipses** for a different look. Mix rectangles and ellipses connected by arrows and you have the beginnings of a flowchart or mind map.

## Add Text to the Canvas

Select the **text tool** and click anywhere on the canvas. A text cursor appears. Type a label — for example, "Main Idea" — and click away to place it. You can also double-click inside any shape to add text directly to that shape.

Go back to your rectangles and double-click inside each one. Type a label in each — maybe "Step 1" and "Step 2." The text becomes part of the shape and moves with it.

Text on the canvas is fully searchable. When you use Cmd+K search later, OmniCanvas will find text placed directly on shapes as well as text in sticky notes and titles.

## Place Sticky Notes

Sticky notes are different from canvas text. They are floating panels that support rich text and come in six colors: yellow, blue, green, pink, purple, and orange.

Add a sticky note using the sticky note tool. Click on the canvas to place it, then start typing. Write a few sentences — maybe a description of what your diagram represents or some notes about what each step involves.

Try adding a second sticky note in a different color. Color-coding sticky notes is a great way to visually distinguish types of content. For example:

- **Yellow** for general notes
- **Blue** for questions you need to answer
- **Green** for completed items or decisions
- **Pink** for warnings or blockers
- **Purple** for creative ideas
- **Orange** for action items

Position your sticky notes near the relevant parts of your diagram. This spatial proximity makes it obvious which notes relate to which elements.

## Arrange Your Canvas

Now step back and look at what you have created. You should have several shapes connected by arrows, some canvas text, and a couple of sticky notes. This is a good time to arrange everything into a layout that makes sense.

Select multiple elements by clicking and dragging a selection box around them. You can then move the entire group at once. Use this to cluster related items together.

Zoom out to see your entire canvas at once. Does the layout tell a story? Is the flow clear? Spatial arrangement communicates meaning — items that are close together feel related, and arrows show direction and dependency.

## Save and Revisit

OmniCanvas saves your work automatically. Every change you make is persisted to local storage immediately. If you have cloud sync enabled, your canvas also syncs to the cloud so you can access it from other devices.

Close the canvas and return to your notes list. You should see your new note with its title. Open it again to confirm everything is exactly as you left it.

## Practice Ideas

Now that you know the basics, here are some quick projects to practice with:

1. **Draw a simple flowchart** for a process you follow regularly, like your morning routine or a work approval process
2. **Create a mind map** with a central concept and branches radiating outward, each with sticky notes for details
3. **Sketch a project plan** with phases as rectangles, dependencies as arrows, and sticky notes for key deliverables
4. **Map out a decision** with options as shapes and pros and cons as sticky notes in different colors

Each of these exercises will help you build muscle memory with the canvas tools. The more you use them, the more naturally you will reach for shapes, arrows, and sticky notes when capturing your thoughts.
`,
  },
  {
    slug: "10-things-first-week-omnicanvas",
    title: "10 Things to Try in Your First Week with OmniCanvas",
    description:
      "A checklist of features to explore in your first week, from basic canvas drawing to the knowledge graph, to help you discover what OmniCanvas can do.",
    date: "2027-03-07",
    readTime: "7 min read",
    category: "Getting Started",
    content: `
## Your First Week Checklist

OmniCanvas has a lot of features, and it can be hard to know where to start. This checklist gives you one thing to try each day (plus a few bonus items) so that by the end of your first week, you will have explored the most important capabilities.

Treat this as a guided tour. You do not need to master each feature right away — just try it once so you know it exists and can come back to it when you need it. If you are brand new, our [beginner's tutorial](/blog/getting-started/getting-started-with-omnicanvas) covers the basics first, and once you are comfortable you can [set up your workspace for the long haul](/blog/getting-started/set-up-omnicanvas-workspace).

## 1. Create a Canvas Note and Draw Something

Start with the basics. Create a new note, draw a few shapes, add some arrows, and type text directly on the canvas. The drawing tools are powered by Excalidraw and feel very natural once you get the hang of them.

Try drawing a simple diagram of something familiar — your team structure, the rooms in your house, or the components of a project. The goal is to get comfortable with the shape tools, selection, and moving things around on the infinite canvas.

## 2. Add Sticky Notes in Multiple Colors

Place at least three sticky notes on a canvas, each in a different color. Write real content in them — not just test text. Sticky notes support rich text, so try adding some bold text and bullet points.

The six available colors (yellow, blue, green, pink, purple, and orange) are a powerful organizational tool. Start thinking about what each color could mean in your personal system.

## 3. Create a Folder Structure

Go to the sidebar and create at least three folders. Make at least one of them a nested subfolder. Then move some of your notes into these folders.

A good starting structure might be areas of your life (Work, Personal, Learning) or types of content (Projects, References, Daily Notes). Do not overthink it — you can always reorganize later.

## 4. Tag Some Notes

Add tags to at least five notes. Use a mix of broad tags like "important" and specific tags like a project name. Tags work alongside folders to give you flexible cross-cutting organization.

After tagging, try clicking on a tag to filter your notes. This is a quick way to surface everything related to a particular topic regardless of which folder it lives in.

## 5. Try Every View Mode

OmniCanvas offers five different views for your notes. Switch through each one and spend a minute exploring:

- **Grid View** shows visual card previews — great for visual browsing
- **List View** is compact and scannable — best for quickly finding a specific note
- **Table View** gives you sortable columns — useful when you need to sort by date or see metadata at a glance
- **Kanban View** lets you organize notes into columns — perfect for workflows and project tracking
- **Graph View** shows your knowledge graph — reveals connections you might not have noticed

Most users settle on one or two favorite views for daily use, but knowing all five exist means you can switch when the situation calls for it.

## 6. Use Cmd+K Search

Press **Cmd+K** (or Ctrl+K on Windows and Linux) and type a word you know appears in one of your notes. Watch how the search results update as you type, pulling from titles, tags, folder names, canvas text, and sticky note content.

Search becomes your primary navigation tool as your collection grows. Practicing it early makes it second nature.

## 7. Explore the Knowledge Graph

Open the **Graph View** and look at how your notes, folders, and tags are connected. Each node represents a note, folder, or tag, and edges show relationships between them.

Click on a node to highlight its connections. Try adding more tags and folder assignments to your notes, then return to the graph to see how it changes. The graph gets more interesting and more useful as your content grows.

## 8. Try Smart Shapes

Draw a rough circle, rectangle, or triangle on the canvas using the freehand tool. OmniCanvas includes **Smart Shapes** — an AI-powered feature that recognizes your hand-drawn shapes and converts them into clean geometric forms.

This is especially useful when you are brainstorming quickly and want to sketch rough diagrams that still look polished. Draw loosely and let Smart Shapes clean up after you.

## 9. Pin a Note and Use Bulk Actions

Find a note you refer to frequently and **pin it**. Pinned notes always appear at the top of your notes list, regardless of sort order. This is the fastest way to keep important notes within reach.

Then try bulk actions: select multiple notes at once and perform an action on all of them, like moving them to a folder, adding a tag, or sending them to the trash. Bulk actions save enormous time when you are reorganizing.

## 10. Export a Note to PDF

Open one of your canvas notes and use the **Export to PDF** option. This generates a clean PDF of your canvas that you can share with anyone, even if they do not use OmniCanvas.

This is particularly useful for sharing diagrams, meeting notes, or brainstorms with colleagues who prefer static documents.

## Bonus: Enable Cloud Sync

If you want your notes available across devices, enable **cloud sync** in the settings. Create an account (or sign in if you already have one), and your notes will sync to the cloud automatically. This also serves as a backup for all your work.

## Bonus: Toggle Dark Mode

Open settings and switch to **dark mode**. Many users prefer it for extended working sessions, especially in the evening. The entire interface — including the canvas — adapts to a dark color scheme that reduces eye strain.

## Wrapping Up

You do not need to master all of these features immediately. The goal of this first week is awareness. Now you know what OmniCanvas can do, and when a need arises, you will remember the feature that fits. Come back to this list anytime you want to explore a feature you skipped or revisit one you want to understand better.
`,
  },
  {
    slug: "set-up-omnicanvas-workspace",
    title: "How to Set Up Your OmniCanvas Workspace for Maximum Productivity",
    description:
      "Best practices for setting up your folder structure, choosing a tagging system, configuring dark mode, and establishing a workflow.",
    date: "2027-03-14",
    readTime: "7 min read",
    category: "Getting Started",
    content: `
## Why Setup Matters

A well-organized workspace is the difference between a notetaking tool that saves you time and one that adds friction. Spending thirty minutes setting up OmniCanvas now will save you hours of searching and reorganizing later. New to the app? Run through the [beginner's tutorial](/blog/getting-started/getting-started-with-omnicanvas) first, then come back here — and once your workspace is set, the [keyboard shortcuts and power tips](/blog/getting-started/omnicanvas-keyboard-shortcuts-power-tips) will help you move through it faster.

This guide covers the four pillars of a productive OmniCanvas workspace: folder structure, tagging system, display preferences, and daily workflow.

## Design Your Folder Structure

Folders in OmniCanvas are hierarchical — you can nest them as deep as you like. But resist the urge to create deeply nested structures. Two or three levels is the sweet spot for most people.

Here are three proven approaches:

### The Areas Approach

Organize by areas of responsibility in your life:

- **Work** — with subfolders for each project or client
- **Personal** — with subfolders for health, finance, hobbies
- **Learning** — with subfolders for courses, books, skills

### The PARA Approach

Based on Tiago Forte's popular system:

- **Projects** — active efforts with a defined end date
- **Areas** — ongoing responsibilities with no end date
- **Resources** — reference material for future use
- **Archive** — completed or inactive items

### The Simple Approach

If you prefer minimal structure:

- **Active** — everything you are currently working on
- **Reference** — information you need to look up
- **Archive** — things you are done with

Choose the approach that feels most natural. You can always reorganize later — OmniCanvas makes it easy to move notes between folders with drag and drop or bulk actions.

## Build a Tagging System

Tags work alongside folders to give you a second dimension of organization. While folders answer "where does this belong," tags answer "what is this about."

### Keep Tags Flat

Unlike folders, tags work best when they are not hierarchical. A flat list of tags is easier to remember and faster to apply. Aim for 10 to 20 tags that cover your most common categories.

### Use Consistent Naming

Pick a convention and stick with it. Some options:

- **Lowercase with hyphens:** project-alpha, meeting-notes, action-item
- **Capitalized:** Project Alpha, Meeting Notes, Action Item
- **Prefixed by type:** type/meeting, status/in-progress, priority/high

The format matters less than consistency. Whatever you choose, apply it uniformly so your tags are predictable and easy to search.

### Suggested Starter Tags

Here is a set of general-purpose tags to get you started:

- **meeting-notes** — notes from meetings and calls
- **action-item** — things you need to do
- **reference** — information you want to look up later
- **idea** — creative concepts and brainstorms
- **decision** — choices made and the reasoning behind them
- **in-progress** — work that is actively underway
- **important** — high-priority items you want to surface quickly

You can add more specific tags as needed, but starting with a small set prevents tag sprawl.

## Configure Your Display

### Choose Your Default View

OmniCanvas offers five views: grid, list, table, kanban, and graph. Experiment with each one, then choose a default that matches your primary use case:

- **Grid** if you are a visual thinker who wants to see canvas previews
- **List** if you process notes quickly and want maximum density
- **Table** if you like spreadsheet-style sorting and filtering
- **Kanban** if you manage projects with distinct stages
- **Graph** if you want to discover connections between ideas

You can always switch views, so your default is just a starting point.

### Dark Mode vs Light Mode

OmniCanvas supports both dark and light modes. Dark mode reduces eye strain in low-light environments and many users find it easier to focus with. Light mode can be better for detailed color work on the canvas.

Toggle dark mode in the settings. The switch is instant and affects the entire interface including the canvas background.

### Pin Your Most-Used Notes

If there are notes you return to daily — a project dashboard, a running task list, or a quick reference sheet — **pin them**. Pinned notes stick to the top of your notes list regardless of sort order, giving you one-click access to your most important canvases.

## Establish a Daily Workflow

Tools only help if you actually use them. Here is a simple daily workflow to build the OmniCanvas habit:

### Morning (5 minutes)

1. Open OmniCanvas and review your pinned notes
2. Check any notes tagged "action-item" or "in-progress"
3. Create a new canvas for the day if you use daily notes

### During the Day (as needed)

1. Capture new ideas immediately — press the new note button and sketch or type whatever comes to mind
2. Use sticky notes for quick thoughts that relate to an existing canvas
3. Tag and folder-assign notes as you create them, not later

### Evening (5 minutes)

1. Review notes created today
2. Add tags or move notes to the correct folders if you skipped that step earlier
3. Pin anything you need for tomorrow

### Weekly (15 minutes)

1. Open the knowledge graph and look for unexpected connections
2. Archive or trash notes that are no longer relevant
3. Review your folder and tag structure — add or merge as needed

## Set Up Cloud Sync

If you use OmniCanvas on more than one device, enable **cloud sync** early. Go to settings, create an account or sign in, and sync will begin automatically. This ensures your workspace is identical everywhere and also serves as a cloud backup.

Sync happens on login and whenever you press the "Sync Now" button in settings. Get in the habit of syncing before you close the app, especially if you plan to pick up on a different device.

## Start Simple, Evolve Over Time

The biggest mistake new users make is trying to build the perfect system on day one. Start with a minimal folder structure, a handful of tags, and a simple daily routine. As you use OmniCanvas and discover your patterns, refine your setup gradually.

The best workspace is one you actually use. Keep it simple enough that capturing a new note never feels like a chore, and organized enough that finding an old note never takes more than a few seconds.
`,
  },
  {
    slug: "omnicanvas-keyboard-shortcuts-power-tips",
    title: "OmniCanvas Keyboard Shortcuts and Power User Tips",
    description:
      "Keyboard shortcuts, power-user workflows and tips for working faster in OmniCanvas — including the Excalidraw canvas shortcuts for shapes, arrows and text.",
    date: "2027-03-21",
    readTime: "5 min read",
    category: "Getting Started",
    content: `
## Speed Up Your Workflow

Keyboard shortcuts are the fastest way to level up your OmniCanvas productivity. Every second saved on navigation and tool switching adds up over weeks and months. This guide covers the shortcuts you should learn first, the canvas-specific shortcuts that make drawing faster, and power user tips for common workflows. If you are still learning the basics, start with the [beginner's tutorial](/blog/getting-started/getting-started-with-omnicanvas); to organize what you capture, see [how to set up your workspace](/blog/getting-started/set-up-omnicanvas-workspace).

## Essential App Shortcuts

These shortcuts work anywhere in OmniCanvas:

- **Cmd+K** (Ctrl+K on Windows/Linux) — Open universal search. This is the single most important shortcut to learn. It searches across note titles, tags, folder names, canvas text, and sticky note content.
- **Cmd+N** — Create a new note instantly without reaching for the mouse.
- **Cmd+Z** — Undo your last action. Works on the canvas for drawing operations and in text fields.
- **Cmd+Shift+Z** — Redo an undone action.

Learn Cmd+K first. Once you are comfortable searching for any note in under two seconds, you will find yourself organizing less aggressively because you can always find what you need.

## Canvas Drawing Shortcuts

When you are on the canvas, single-key shortcuts switch between drawing tools without clicking the toolbar. These come from Excalidraw and will feel natural after a few minutes of practice:

- **V** — Select tool (the default). Click to select elements, drag to create a selection box.
- **R** — Rectangle tool. Click and drag to draw a rectangle.
- **O** — Ellipse (oval) tool. Click and drag to draw a circle or ellipse.
- **A** — Arrow tool. Click and drag to draw an arrow between points or shapes.
- **L** — Line tool. Draw straight lines and polylines.
- **D** — Diamond tool. Draw diamond shapes, useful for decision nodes in flowcharts.
- **T** — Text tool. Click anywhere to start typing directly on the canvas.
- **P** — Pencil (freehand drawing) tool. Draw freely with your mouse or trackpad.
- **E** — Eraser tool. Click on elements to remove them from the canvas.

### Canvas Navigation Shortcuts

- **Cmd+Plus** or **Ctrl+Plus** — Zoom in
- **Cmd+Minus** or **Ctrl+Minus** — Zoom out
- **Cmd+0** or **Ctrl+0** — Reset zoom to 100 percent
- **Cmd+Shift+1** — Zoom to fit all content on screen
- **Space+Drag** — Pan the canvas without switching tools
- **Scroll wheel** — Zoom in and out (when holding Ctrl) or scroll vertically

### Element Manipulation

- **Cmd+D** — Duplicate the selected element. Much faster than copy and paste when you need multiple copies of the same shape.
- **Cmd+G** — Group selected elements so they move and resize together.
- **Cmd+Shift+G** — Ungroup a grouped selection.
- **Delete or Backspace** — Remove the selected element from the canvas.
- **Cmd+A** — Select all elements on the canvas.
- **Cmd+C** and **Cmd+V** — Copy and paste elements. Works within OmniCanvas and you can paste images from your clipboard onto the canvas.

## Power User Tips

### Tip 1: Use Search as Navigation

Stop browsing through folders to find notes. Press Cmd+K, type two or three characters, and jump directly to the note you need. This works even if you cannot remember the exact title — search matches against tags, folder names, and content inside sticky notes and canvas text.

### Tip 2: Build Templates as Pinned Notes

If you create the same type of canvas repeatedly — meeting notes, weekly reviews, project plans — build a template canvas once with your preferred layout, shapes, and sticky note placeholders. Pin it for quick access. When you need a new instance, duplicate the template note and rename it.

### Tip 3: Color-Code Sticky Notes by Action Type

Assign a consistent meaning to each sticky note color across all your canvases. For example, yellow for information, green for decisions, pink for open questions, and orange for action items. Over time, you will be able to scan any canvas and immediately see what needs attention.

### Tip 4: Use Bulk Actions for Reorganization

When you need to reorganize multiple notes — moving them to a new folder, adding a tag, or sending old notes to trash — select them all at once using bulk selection. This is dramatically faster than editing notes one at a time. Bulk actions work in grid, list, and table views.

### Tip 5: Let Smart Shapes Clean Up Your Sketches

When brainstorming quickly, do not bother switching to the rectangle or ellipse tool. Just draw rough shapes with the pencil tool and let **Smart Shapes** recognize and convert them into clean geometric forms. This keeps you in a creative flow state instead of switching tools constantly.

### Tip 6: Use the Knowledge Graph for Weekly Reviews

At the end of each week, open the **Graph View** and explore the connections between your recent notes. Look for clusters of notes that might benefit from a new folder, orphaned notes that need tags, or unexpected connections between projects. The graph turns a routine review into a discovery process.

### Tip 7: Master the Zoom Shortcuts

Zoom to fit (Cmd+Shift+1) is your best friend on a busy canvas. It instantly adjusts the zoom level so all your content is visible on screen. Use it whenever you lose your bearings or want to see the full picture after working on a detailed area.

## Building Muscle Memory

You do not need to memorize every shortcut at once. Start with these three and practice them until they are automatic:

1. **Cmd+K** for search
2. **R**, **O**, **A**, **T** for drawing tools
3. **Cmd+D** for duplicating elements

Once those feel natural, add more shortcuts from the lists above. Within a couple of weeks, you will be navigating OmniCanvas almost entirely from the keyboard, and it will feel significantly faster than clicking through menus.
`,
  },
  {
    slug: "using-omnicanvas-web-app",
    title: "Using OmniCanvas on the Web: Tips for Non-Mac Users",
    description:
      "How to get the most out of the OmniCanvas web app on Windows, Linux or any device: sync setup, browser tips and parity with the desktop app.",
    date: "2027-03-28",
    readTime: "6 min read",
    category: "Getting Started",
    content: `
## OmniCanvas Works Everywhere

While OmniCanvas offers a native macOS desktop app built with Tauri, the full experience is also available as a web app that runs in any modern browser. Whether you are on Windows, Linux, ChromeOS, or even a tablet, you can access the same infinite canvas, the same organizational tools, and the same knowledge graph — no installation required.

This guide covers how to get the most out of OmniCanvas in the browser, including setup tips, performance advice, and the few differences you should know about compared to the desktop app. New here? The [beginner's tutorial](/blog/getting-started/getting-started-with-omnicanvas) walks through the essentials, and the [keyboard shortcuts guide](/blog/getting-started/omnicanvas-keyboard-shortcuts-power-tips) maps Cmd to Ctrl for Windows and Linux.

## Accessing the Web App

Open your browser and navigate to **app.omnicanvas.com**. You can start using OmniCanvas immediately — no account required. Your notes are saved to your browser's local storage, so they persist between sessions.

For the best experience, use a Chromium-based browser (Chrome, Edge, Brave, Arc) or Firefox. Safari works as well, though Chromium browsers tend to offer the smoothest canvas performance.

## Setting Up Cloud Sync

If you plan to use OmniCanvas on more than one device — or if you simply want a cloud backup — set up sync right away. Open the settings panel and create an account with your email and a password. Once signed in, your notes sync to the cloud.

Cloud sync is especially important for web app users because browser local storage can be cleared by the browser under certain conditions (clearing browsing data, storage pressure in some browsers, or switching browser profiles). With cloud sync enabled, your data is safe regardless of what happens to your local storage.

To sync, click **Sync Now** in the settings panel. Sync also happens automatically when you sign in. If you switch between devices regularly, make it a habit to sync before closing the app.

## Keyboard Shortcuts for Windows and Linux

Most OmniCanvas shortcuts use **Cmd** on macOS. On Windows and Linux, substitute **Ctrl** for Cmd:

- **Ctrl+K** — Open universal search
- **Ctrl+N** — Create a new note
- **Ctrl+Z** — Undo
- **Ctrl+Shift+Z** — Redo
- **Ctrl+D** — Duplicate selected element on the canvas
- **Ctrl+G** — Group selected elements
- **Ctrl+A** — Select all canvas elements

Canvas tool shortcuts are the same across all platforms since they are single keys:

- **R** for rectangle, **O** for ellipse, **A** for arrow, **L** for line
- **D** for diamond, **T** for text, **P** for pencil, **E** for eraser
- **V** to switch back to the select tool

These work identically in the web app and the desktop app.

## Browser Tips for the Best Experience

### Use Fullscreen Mode

Press **F11** (or Fn+F11 on some keyboards) to enter fullscreen mode in your browser. This hides the address bar and browser tabs, giving you maximum screen real estate for the canvas. The experience becomes very close to a native desktop app.

### Create a Browser Shortcut

Most browsers let you "install" a web app or create a shortcut that opens it in its own window:

- **Chrome/Edge:** Click the three-dot menu, then "Install app" or "Create shortcut" with "Open as window" checked
- **Firefox:** Bookmark the page and drag the bookmark to your taskbar or desktop

This gives you a dedicated window for OmniCanvas without browser tabs and navigation bars competing for space.

### Avoid Clearing Local Storage

If you are not using cloud sync, be cautious about clearing your browser data. OmniCanvas stores your notes in local storage, and a "clear all browsing data" action may remove them. If you must clear data, make sure to exclude site data for app.omnicanvas.com, or enable cloud sync first as a safety net.

### Disable Browser Shortcuts That Conflict

Some browser shortcuts can interfere with OmniCanvas. For example, Ctrl+D in many browsers opens the "Add Bookmark" dialog instead of duplicating a canvas element. When you install OmniCanvas as a standalone web app window (using the method above), most of these conflicts go away because the browser chrome is removed.

## Feature Parity with the Desktop App

The web app and the macOS desktop app share the same core codebase. This means nearly everything works identically:

- **Infinite canvas with all drawing tools** — rectangles, ellipses, arrows, lines, diamonds, freehand drawing, text, and eraser
- **Sticky notes** in six colors with rich text
- **Folders and tags** for organization
- **All five views** — grid, list, table, kanban, and graph
- **Knowledge graph** with full interactivity
- **Smart Shapes** for AI shape recognition
- **Universal search** with Cmd/Ctrl+K
- **Dark mode** toggle
- **PDF export**
- **Evernote import**
- **Cloud sync**
- **Pinning, bulk actions, and trash with restore**

The desktop app does have a few additional capabilities that come from the native Tauri shell, such as system-level menu bar integration and native file dialogs. However, all note-taking, drawing, and organizational features are fully available in the browser.

## Performance Tips

The canvas is the most performance-sensitive part of OmniCanvas. Here are a few tips to keep it smooth:

- **Close unused browser tabs.** Each tab consumes memory, and the canvas benefits from having more available.
- **Use hardware acceleration.** Make sure it is enabled in your browser settings (it usually is by default). This lets the canvas use your GPU for rendering.
- **Keep canvases focused.** If a single canvas has hundreds of elements and starts to feel slow, consider splitting it into multiple notes linked by tags or folder organization.
- **Update your browser.** Browser makers regularly improve canvas and JavaScript performance. Running the latest version ensures you get these improvements.

## Working Across Devices

One of the biggest advantages of the web app is that you can access your workspace from any device. Here is a smooth multi-device workflow:

1. **Enable cloud sync** on every device where you use OmniCanvas
2. **Sync before switching** — click Sync Now before closing the app on one device
3. **Sync after opening** — sign in and sync when you open the app on a new device
4. **Use the same account** everywhere so all your notes, folders, and tags stay unified

This workflow keeps your workspace consistent whether you are on your Windows desktop at work, your Linux laptop at home, or your Mac at a coffee shop. The spatial canvas, all your organizational structure, and every sticky note will be right where you left them.
`,
  },
];
